/**
 * PaCartManager — reads/counts/clears the pa_basket (sessionStorage) and keeps
 * cart badges in sync.
 *
 * Basket lines are only ever written by the native basket API in frontend.js
 * (addToBasket / removeFromBasket / clearBasket), which stores a plain array
 * of lines shaped { id, ident, name, quantity, ... } with quantity >= 1 and
 * dispatches pa_basket_update on every mutation.
 *
 * Events:
 * - listens:    pa_basket_update  (fired by frontend.js on every basket mutation)
 * - dispatches: pa:cart:count     detail: { count, source }
 *               pa:cart:cleared   detail: { source }
 */

const BASKET_KEY = 'pa_basket';

/**
 * Cart UX config from init.php (single source of the defaults). THE accessor
 * — four modules used to carry byte-identical copies of this read, with two
 * different existence-guard idioms.
 */
export function cartUx() {
    return ( typeof JQUERY_VARS !== 'undefined' && JQUERY_VARS.cartUx ) || {};
}

export function cartLabels() {
    return cartUx().labels || {};
}

//init.php already defaults cart_page_url; the fallback here only covers a
//page rendered without the localize script (tests, previews).
export function cartUrl() {
    return ( cartUx().stickyBasket || {} ).cartUrl || '/kurv/';
}

/**
 * Subscribe to basket mutations, coalesced to one call per frame. THE
 * re-render trigger — the per-module copies of this rAF scheduler had
 * already been written four times. Callers still guard their own bind-once
 * (init may run twice); this owns only the scheduling.
 *
 * @param {Function} fn Called once per burst of pa_basket_update events.
 */
export function onBasketUpdate( fn ) {
    let scheduled = false;
    window.addEventListener( 'pa_basket_update', function() {
        if ( scheduled ) {
            return;
        }
        scheduled = true;
        ( window.requestAnimationFrame || setTimeout )( function() {
            scheduled = false;
            fn();
        } );
    } );
}

export function readBasket() {
    let lines = null;
    try {
        lines = JSON.parse( window.sessionStorage.getItem( BASKET_KEY ) );
    } catch ( e ) { }
    return Array.isArray( lines ) ? lines : [];
}

//Deliberately counts EVERY line, including 'remove' (cancellations) and
//'config' (mix changes): those are pending orders the customer still has to
//submit on the cart page, so the badge/sticky bar must surface them.
export function countBasket( lines ) {
    let total = 0;
    for ( const line of lines || [] ) {
        total += Number( line && line.quantity ) || 0;
    }
    return total;
}

/**
 * THE shared answer to "is this product in the cart" — the per-block copies
 * had drifted into three different definitions.
 *
 * @param {*} pcid Product configuration id (string or number).
 * @return {'add'|'removal'|null} 'add' for any ordering action
 *         (add/update/upgrade/downgrade/config), 'removal' for a
 *         cancellation line, null when the product is not in the cart.
 */
export function getCartState( pcid ) {
    const wanted = String( pcid );
    for ( const line of readBasket() ) {
        if ( line && String( line.id ) === wanted ) {
            return line.action === 'remove' ? 'removal' : 'add';
        }
    }
    return null;
}

export function getCount() {
    return countBasket( readBasket() );
}

function updateBadges( count ) {
    //Only the documented .pa-cart-badge — a bare .pa-badge selector would
    //hijack any site element that happens to use that name.
    document.querySelectorAll( '.pa-cart-badge' ).forEach( badge => {
        if ( badge.textContent !== String( count ) ) {
            badge.textContent = String( count );
        }
        badge.classList.toggle( 'is-zero', count <= 0 );
    } );
    document.documentElement.classList.toggle( 'pa-basket-has', count > 0 );
    document.documentElement.classList.toggle( 'pa-basket-empty', count <= 0 );
}

function fire( name, detail ) {
    window.dispatchEvent( new CustomEvent( name, { detail: detail || {} } ) );
}

export function sync( source ) {
    const count = getCount();
    updateBadges( count );
    fire( 'pa:cart:count', { count, source: source || 'cart-manager' } );
    return count;
}

export function clear( options ) {
    // clearBasket dispatches pa_basket_update, which runs sync() synchronously.
    window.clearBasket();
    fire( 'pa:cart:cleared', { source: ( options && options.source ) || 'cart-manager-clear' } );
    //The basket block (basket.js) renders its list on page load only, so a
    //reload is needed wherever a list is present — for EVERY clear caller
    //(cart-page button, sticky bar), or the customer keeps a rendered list
    //of items that no longer exist. Everything else (badges, cards, sticky
    //bar) has already re-rendered from pa_basket_update.
    if ( document.querySelector( '.basket-container' ) ) {
        window.location.reload();
    }
}

let initialized = false;

export function init() {
    //Idempotent: a double init would stack listeners and double the clear
    //handling (two reloads).
    if ( initialized ) {
        return sync( 'init' );
    }
    initialized = true;
    //Coalesced like every other basket listener: single-select evicts other
    //lines one removeFromBasket at a time, so one click can burst K events —
    //one badge/count pass per frame suffices.
    onBasketUpdate( function() {
        sync( 'pa_basket_update' );
    } );

    // The cart page renders a "Tøm kurv" button (.pa-clear-cart-btn) as plain
    // markup — the plugin owns its behavior. Reload so page-rendered basket
    // lists (basket block / snippet page) redraw from the empty basket.
    document.addEventListener( 'click', function( event ) {
        const button = event.target && event.target.closest && event.target.closest( '.pa-clear-cart-btn' );
        if ( ! button ) {
            return;
        }
        event.preventDefault();
        clear( { source: 'clear-cart-button' } );
    } );

    return sync( 'init' );
}

const api = { readBasket, countBasket, getCount, getCartState, cartUx, cartLabels, cartUrl, onBasketUpdate, sync, clear, init };

export default api;
